import React from 'react'
import { PredictionTypeEnum, ScoreEnum, getValueByPredictionType } from '../types/prediction'
import { useTranslation } from '../hooks/use-i18n'

interface SelectResultModalProps {
  title: string
  isOpen: boolean
  type: PredictionTypeEnum
  onClose: () => void
  onSelect: (score: ScoreEnum) => void
  variant: 'a' | 'b'
}

const SelectResultModal: React.FC<SelectResultModalProps> = ({ 
  title, 
  isOpen, 
  onClose, 
  onSelect, 
  type, 
  variant 
}) => {
  const { t } = useTranslation()
  if (!isOpen) return null

  const scores = getValueByPredictionType(type)

  // Format score for display (reverse for team B)
  const formatScore = (score: ScoreEnum) => {
    if (score === ScoreEnum.FiveAndMore) {
      return t('predictions.fiveAndMore')
    }
    if (score === ScoreEnum.FiveFiveMore) {
      return t('predictions.fiveFiveMore')
    }
    return variant === 'a' ? score : score.split('-').reverse().join('-')
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 sm:p-6">
      <div className="blue-sky-card rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 w-full max-w-md sm:max-w-lg shadow-2xl animate-scale-in flex flex-col overflow-hidden shrink-0 my-auto min-h-0 max-h-[70vh] sm:max-h-[75vh]">
        <div className="text-center mb-4 sm:mb-5 flex-shrink-0">
          <h2 className="text-base sm:text-lg lg:text-xl font-bold text-white">{title}</h2>
          <p className="text-white/60 text-xs sm:text-sm mt-1">{t('predictions.selectScore')}</p>
        </div>

        {/* Single-column scrollable score list */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden -mx-1 px-1 overscroll-contain">
          <div className="flex flex-col gap-3">
            {scores.map((score) => {
              const isFiveAndMore = score === ScoreEnum.FiveAndMore || score === ScoreEnum.FiveFiveMore
              return (
                <button
                  key={score}
                  onClick={() => onSelect(score)}
                  className={`
                    w-full px-4 py-3 sm:py-3.5
                    bg-gradient-to-r from-sky-500 to-blue-600 
                    hover:from-sky-600 hover:to-blue-700 
                    active:scale-[0.98]
                    text-white rounded-xl 
                    transition-all duration-200 
                    shadow-md hover:shadow-lg 
                    font-semibold text-sm sm:text-base text-center
                    ${isFiveAndMore ? 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700' : ''}
                  `}
                >
                  {formatScore(score)}
                </button>
              )
            })}
          </div>
        </div>

        <div className="mt-4 sm:mt-5 pt-4 sm:pt-5 border-t border-white/20 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 sm:px-6 sm:py-3 bg-white/10 hover:bg-white/20 active:bg-white/30 text-white rounded-xl transition-all duration-200 font-semibold text-sm sm:text-base"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

export default SelectResultModal
